import { ProcedureState } from "@/backend/models/procedureInstances/procedureInstanceBase";
import { useGlobalStore } from "@/frontend/store/GlobalStore";
import { useParameterStore } from "@/frontend/store/ParameterStore";
import { useProcedureInstanceStore } from "@/frontend/store/ProcedureInstanceStore";
import { useTreeStore } from "@/frontend/store/TreeStore";
import { I18n } from "@/i18n/I18n";
import { Status } from "@/types/containerTypes";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Modal } from "react-bootstrap";
import { CONTEXT_LOG_SUBJECT_TYPES } from "@/types/contextLogTypes";
import { useNavigateToNextStep } from "@/frontend/hooks/useGetNextStep";
import { ContextLogInput } from "../contextLogs/ContextLogInput";

export function StepFailureModal(props: { stepId: string, isDisabled?: boolean, onUpdateStatus?: () => void }) {
  const { stepId, isDisabled, onUpdateStatus } = props;

  const failStep = useTreeStore(state => state.failStep);
  const updateProcedure = useProcedureInstanceStore(state => state.updateProcedureInstance);
  const procedure = useProcedureInstanceStore(state => state.procedureInstance);
  const addContextLog = useParameterStore(state => state.addContextLog);
  const userInfo = useGlobalStore(state => state.userClass);
  const navigate = useNavigate();
  const [showContextLogModal, setShowContextLogModal] = useState(false);
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState('');
  const [userEmail, setUserEmail] = useState(userInfo?.email || '');
  const [userEmailError, setUserEmailError] = useState('');
  const containerLookup = useTreeStore((state) => state.containerLookup);
  const step = containerLookup[stepId];
  const navigateToNextStep = useNavigateToNextStep(step);
  const onWizardView = useLocation().pathname.includes('wizard');

  const abortProcedureInvalid = async () => {
    if (!note || note.length === 0) {
      setNoteError(I18n.get('reasonForFailStepRequired'));
      return;
    }
    
    if (!userEmail || userEmail.length === 0) {
      setUserEmailError(I18n.get('userEmailRequired'));
      return;
    }

    if (procedure) {
      await updateProcedure({procedureInstance: { ...procedure, status: Status.ABORTED, state: ProcedureState.INVALID }});
      await failStep(stepId);
      await addContextLog({
        subjectId: stepId,
        subjectType: CONTEXT_LOG_SUBJECT_TYPES.STEP,
        note,
        userEmail,
      });
      clearForm();
      void navigate(`/procedures/wizard/${procedure.id}/publish`);
    }
  };

  const onClickFailStep = async () => {
    if (!note || !userEmail) {
      if (!note || note.length === 0) {
        setNoteError(I18n.get('reasonForFailStepRequired'));
      }
      if (!userEmail || userEmail.length === 0) {
        setUserEmailError(I18n.get('userEmailRequired'));
      }
      return;
    }
    void addContextLog({
      subjectId: stepId,
      subjectType: CONTEXT_LOG_SUBJECT_TYPES.STEP,
      note,
      userEmail,
    });
    clearForm();
    setShowContextLogModal(false);
    await failStep(step.id);
    onUpdateStatus?.();
    if (onWizardView) {
      void navigateToNextStep();
    }
  };

  const onCancel = () => {
    clearForm();
    setShowContextLogModal(false);
  };

  const clearForm = () => {
    setNote('');
    setNoteError('');
    setUserEmail(userInfo?.email || '');
    setUserEmailError('');
  };

  return (
    <>
      <button
        className='btn btn-sm btn-danger text-nowrap my-auto'
        onClick={() => setShowContextLogModal(true)}
        type="button"
        aria-label={I18n.get('failStep')}
        disabled={isDisabled}
      >
        {I18n.get('failStep')}
      </button>

      { showContextLogModal && (
        <Modal show onHide={onCancel} size='lg'>
        <Modal.Header className="bg-danger text-white">
          <Modal.Title>{I18n.get('failStep')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ContextLogInput note={note} setNote={setNote} noteError={noteError} userEmail={userEmail} setUserEmail={setUserEmail} userEmailError={userEmailError} contextLogLabelText={I18n.get('reasonForFailStep')} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={onCancel}>{I18n.get('cancel')}</Button>
          <Button variant='danger' onClick={() => void abortProcedureInvalid()}>{I18n.get('abortProcedureInvalid')}</Button>
          <Button variant='warning' onClick={() => void onClickFailStep()}>{I18n.get('failStepAndContinueProcedure')}</Button>
        </Modal.Footer>
      </Modal>
      )}
    </>
  );
}
